/**
 * Product Gallery Module
 * Handles product image gallery functionality including main gallery, thumbnails, and navigation
 */

export class ProductGallery {
  constructor(options = {}) {
    this.options = {
      mainGallerySelector: '[data-main-gallery]',
      galleryTrackSelector: '[data-gallery-track]',
      productImageSelector: '[data-product-image]',
      thumbnailGallerySelector: '[data-thumbnail-gallery]',
      thumbnailTrackSelector: '[data-thumbnail-track]',
      thumbnailButtonSelector: '[data-thumbnail-button]',
      prevButtonSelector: '[data-gallery-prev]',
      nextButtonSelector: '[data-gallery-next]',
      thumbnailPrevSelector: '[data-thumbnail-prev]',
      thumbnailNextSelector: '[data-thumbnail-next]',
      scrollbarContainerSelector: '[data-scrollbar-container]',
      scrollbarThumbSelector: '[data-scrollbar-thumb]',
      ...options
    };

    this.currentIndex = 0;
    this.totalImages = 0;
    this.thumbnailWidth = 0;
    this.visibleThumbnails = 0;
    this.isGalleryDragging = false;
    this.galleryDragStartX = 0;
    this.galleryDragStartIndex = 0;
    this.enableLoop = false;
    this.isDotsMode = false;

    // DOM elements
    this.mainGallery = null;
    this.galleryTrack = null;
    this.productImages = null;
    this.thumbnailGallery = null;
    this.thumbnailTrack = null;
    this.thumbnailButtons = null;
    this.prevButton = null;
    this.nextButton = null;
    this.thumbnailPrevButton = null;
    this.thumbnailNextButton = null;
    this.scrollbarContainer = null;
    this.scrollbarThumb = null;

    this.init();
  }

  /**
   * Initialize the gallery
   */
  init() {
    console.log('🎨 [Gallery] Initializing product gallery...');
    
    this.getElements();
    this.calculateThumbnailWidth();
    this.bindEvents();
    this.updateGalleryPosition();
    this.updateThumbnailPosition();
    this.updateActiveThumbnail();
    this.updateScrollbar();
    this.updateMainGalleryArrows();
    
    console.log('✅ [Gallery] Gallery initialized successfully with:', {
      totalImages: this.totalImages,
      visibleThumbnails: this.visibleThumbnails,
      currentIndex: this.currentIndex,
      thumbnailWidth: this.thumbnailWidth
    });
  }

  /**
   * Get all required DOM elements
   */
  getElements() {
    this.mainGallery = document.querySelector(this.options.mainGallerySelector);
    this.galleryTrack = document.querySelector(this.options.galleryTrackSelector);
    this.productImages = document.querySelectorAll(this.options.productImageSelector);
    // Check for dots overlay mode first
    const dotsGallery = document.querySelector('[data-dots-overlay]');
    const regularThumbnails = document.querySelector('[data-regular-thumbnails]');
    
    // If both exist (dots_overlay enabled), use regular thumbnails on desktop, dots on mobile
    if (dotsGallery && regularThumbnails) {
      if (window.innerWidth <= 768) {
        // Mobile: use dots
        this.thumbnailGallery = dotsGallery;
        this.thumbnailTrack = document.querySelector('[data-dots-overlay] [data-thumbnail-track]');
        this.thumbnailButtons = document.querySelectorAll('[data-dots-overlay] [data-thumbnail-button]');
      } else {
        // Desktop: use regular thumbnails
        this.thumbnailGallery = regularThumbnails;
        this.thumbnailTrack = document.querySelector('[data-regular-thumbnails] [data-thumbnail-track]');
        this.thumbnailButtons = document.querySelectorAll('[data-regular-thumbnails] [data-thumbnail-button]');
      }
    } else {
      // Fallback to default behavior
      this.thumbnailGallery = regularThumbnails || document.querySelector(this.options.thumbnailGallerySelector);
      this.thumbnailTrack = document.querySelector('[data-regular-thumbnails] [data-thumbnail-track]') || document.querySelector(this.options.thumbnailTrackSelector);
      this.thumbnailButtons = document.querySelectorAll('[data-regular-thumbnails] [data-thumbnail-button]').length > 0 
        ? document.querySelectorAll('[data-regular-thumbnails] [data-thumbnail-button]')
        : document.querySelectorAll(this.options.thumbnailButtonSelector);
    }
    this.prevButton = document.querySelector(this.options.prevButtonSelector);
    this.nextButton = document.querySelector(this.options.nextButtonSelector);
    this.thumbnailPrevButton = document.querySelector('[data-regular-thumbnails] [data-thumbnail-prev]') || document.querySelector(this.options.thumbnailPrevSelector);
    this.thumbnailNextButton = document.querySelector('[data-regular-thumbnails] [data-thumbnail-next]') || document.querySelector(this.options.thumbnailNextSelector);
    this.scrollbarContainer = document.querySelector('[data-regular-thumbnails] [data-scrollbar-container]') || document.querySelector(this.options.scrollbarContainerSelector);
    this.scrollbarThumb = document.querySelector('[data-regular-thumbnails] [data-scrollbar-thumb]') || document.querySelector(this.options.scrollbarThumbSelector);
    
    // Check if loop is enabled
    this.enableLoop = this.mainGallery && this.mainGallery.dataset.enableLoop === 'true';
    console.log('🎨 [Gallery] Loop enabled:', this.enableLoop);
    
    // Check if dots mode is enabled (mobile only)
    this.isDotsMode = dotsGallery && this.thumbnailGallery === dotsGallery && window.innerWidth <= 768;
    console.log('🎨 [Gallery] Dots mode enabled:', this.isDotsMode, 'Screen width:', window.innerWidth, 'Using:', this.thumbnailGallery);
    
    // When dots_overlay is enabled, we have both navigation systems (regular thumbnails + dots)
    this.hasDualNavigation = !!dotsGallery;
    console.log('🎨 [Gallery] Dual navigation enabled:', this.hasDualNavigation);
  }

  /**
   * Calculate thumbnail dimensions dynamically
   */
  calculateThumbnailWidth() {
    if (this.thumbnailButtons.length > 0 && this.thumbnailTrack) {
      this.totalImages = this.thumbnailButtons.length;
      
      if (this.isDotsMode) {
        // In dots mode, each dot is 8px wide with 5px gap
        this.thumbnailWidth = 13; // 8px dot + 5px gap
        this.visibleThumbnails = this.totalImages; // All dots are always visible
      } else {
        const firstThumbnail = this.thumbnailButtons[0];
        const rect = firstThumbnail.getBoundingClientRect();
        
        // Get computed styles to handle CSS custom properties
        const computedStyle = getComputedStyle(firstThumbnail);
        const computedWidth = parseFloat(computedStyle.width);
        const computedHeight = parseFloat(computedStyle.height);
        
        // Use computed width if available, fallback to bounding rect
        this.thumbnailWidth = computedWidth || rect.width;
        
        const containerRect = this.thumbnailTrack.parentElement.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const rawVisible = containerWidth / this.thumbnailWidth;
        this.visibleThumbnails = Math.floor(rawVisible);
      }
      
      // Get current screen size for debugging
      const isMobile = window.innerWidth <= 768;
      
      console.log('🎨 [Gallery] Calculated dimensions:', {
        isDotsMode: this.isDotsMode,
        isMobile,
        screenWidth: window.innerWidth,
        thumbnailWidth: this.thumbnailWidth,
        totalThumbnails: this.totalImages,
        visibleThumbnails: this.visibleThumbnails
      });
    }
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Thumbnail clicks
    if (this.thumbnailButtons && this.thumbnailButtons.length > 0) {
      this.thumbnailButtons.forEach(button => {
        button.addEventListener('click', () => {
          const index = parseInt(button.dataset.index);
          this.goToImage(index);
        });
      });
    }

    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevImage());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextImage());
    }

    // Thumbnail navigation
    if (this.thumbnailPrevButton) {
      this.thumbnailPrevButton.addEventListener('click', () => this.scrollThumbnails(-1));
    }
    if (this.thumbnailNextButton) {
      this.thumbnailNextButton.addEventListener('click', () => this.scrollThumbnails(1));
    }

    // Keyboard navigation
    document.addEventListener('keydown', (event) => {
      if (!this.mainGallery) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.prevImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextImage();
          break;
      }
    });

    // Check if scrollbar is necessary

    // Gallery drag/swipe events
    this.bindDragEvents();

    // Scrollbar events
    this.bindScrollbarEvents();

    // Variant change events
    document.addEventListener('variant:change', (event) => {
      console.log('🎨 [Gallery] Received variant:change event:', event.detail);
      const { variantId } = event.detail;
      const thumbnailIndex = this.findThumbnailForVariant(variantId);
      console.log('🎨 [Gallery] Switching to thumbnail index:', thumbnailIndex, 'for variant:', variantId);
      this.goToImage(thumbnailIndex, true); // Instant scroll for variant changes
    });

    // Variant loaded events
    document.addEventListener('variant:loaded', (event) => {
      console.log('🎨 [Gallery] Received variant:loaded event:', event.detail);
      const { variantId } = event.detail;
      const thumbnailIndex = this.findThumbnailForVariant(variantId);
      console.log('🎨 [Gallery] Switching to thumbnail index:', thumbnailIndex, 'for variant:', variantId);
      this.goToImage(thumbnailIndex, true); // Instant scroll for variant changes
    });

    // Window resize with debouncing for performance
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        console.log('🎨 [Gallery] Handling resize - recalculating dimensions');
        // Re-select the correct thumbnail gallery based on screen size
        const dotsGallery = document.querySelector('[data-dots-overlay]');
        const regularThumbnails = document.querySelector('[data-regular-thumbnails]');
        
        if (dotsGallery && regularThumbnails) {
          if (window.innerWidth <= 768) {
            // Mobile: use dots
            this.thumbnailGallery = dotsGallery;
            this.thumbnailTrack = document.querySelector('[data-dots-overlay] [data-thumbnail-track]');
            this.thumbnailButtons = document.querySelectorAll('[data-dots-overlay] [data-thumbnail-button]');
          } else {
            // Desktop: use regular thumbnails
            this.thumbnailGallery = regularThumbnails;
            this.thumbnailTrack = document.querySelector('[data-regular-thumbnails] [data-thumbnail-track]');
            this.thumbnailButtons = document.querySelectorAll('[data-regular-thumbnails] [data-thumbnail-button]');
          }
        }
        
        // Update dots mode based on screen size
        this.isDotsMode = dotsGallery && this.thumbnailGallery === dotsGallery && window.innerWidth <= 768;
        this.hasDualNavigation = !!dotsGallery;
        this.calculateThumbnailWidth();
        this.updateThumbnailPosition();
      }, 150); // 150ms debounce
    });

    // Thumbnail scroll events
    if (this.thumbnailTrack) {
      this.thumbnailTrack.addEventListener('scroll', () => {
        this.updateScrollbar();
      });
    }
  }
  

  /**
   * Bind drag/swipe events for main gallery
   */
  bindDragEvents() {
    if (!this.mainGallery) return;

    // Mouse events
    this.mainGallery.addEventListener('mousedown', (event) => {
      if (this.totalImages <= 1) return;
      this.startDrag(event.clientX, 'mouse');
    });

    document.addEventListener('mousemove', (event) => {
      if (!this.isGalleryDragging) return;
      this.handleDrag(event.clientX, 'mouse');
    });

    document.addEventListener('mouseup', (event) => {
      if (!this.isGalleryDragging) return;
      this.endDrag(event.clientX, 'mouse');
    });

    // Touch events
    this.mainGallery.addEventListener('touchstart', (event) => {
      if (this.totalImages <= 1) return;
      event.preventDefault();
      this.startDrag(event.touches[0].clientX, 'touch');
    });

    document.addEventListener('touchmove', (event) => {
      if (!this.isGalleryDragging) return;
      event.preventDefault();
      this.handleDrag(event.touches[0].clientX, 'touch');
    });

    document.addEventListener('touchend', (event) => {
      if (!this.isGalleryDragging) return;
      this.endDrag(event.changedTouches[0].clientX, 'touch');
    });
  }

  /**
   * Start dragging
   */
  startDrag(clientX, type) {
    this.isGalleryDragging = true;
    this.galleryDragStartX = clientX;
    this.galleryDragStartIndex = this.currentIndex;
    
    this.galleryTrack.style.transition = 'transform 0.3s ease-out';
    
    if (type === 'mouse') {
      this.mainGallery.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }
    
    console.log('🎨 [Gallery] Started dragging:', { clientX, type });
  }

  /**
   * Handle dragging
   */
  handleDrag(clientX, type) {
    const deltaX = clientX - this.galleryDragStartX;
    const galleryWidth = this.mainGallery.offsetWidth;
    
    const dragProgress = deltaX / galleryWidth;
    const maxProgress = 0.3;
    const clampedProgress = Math.max(-maxProgress, Math.min(maxProgress, dragProgress));
    
    const currentTranslate = -this.currentIndex * 100;
    const dragTranslate = currentTranslate + (clampedProgress * 100);
    this.galleryTrack.style.transform = `translateX(${dragTranslate}%)`;
    
    if (Math.abs(deltaX) > 50) {
      let newIndex = this.currentIndex;
      const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
      
      if (this.enableLoop) {
        // Loop behavior for drag/swipe
        if (deltaX > 0) {
          newIndex = this.currentIndex === 0 ? mainGalleryImageCount - 1 : this.currentIndex - 1;
        } else if (deltaX < 0) {
          newIndex = (this.currentIndex + 1) % mainGalleryImageCount;
        }
      } else {
        // Normal behavior for drag/swipe
        if (deltaX > 0 && this.currentIndex > 0) {
          newIndex = this.currentIndex - 1;
        } else if (deltaX < 0 && this.currentIndex < mainGalleryImageCount - 1) {
          newIndex = this.currentIndex + 1;
        }
      }
      
      if (newIndex !== this.currentIndex) {
        this.goToImage(newIndex);
        this.isGalleryDragging = false;
        
        if (type === 'mouse') {
          this.mainGallery.style.cursor = '';
          document.body.style.userSelect = '';
        }
      }
    }
  }

  /**
   * End dragging
   */
  endDrag(clientX, type) {
    this.isGalleryDragging = false;
    
    if (type === 'mouse') {
      this.mainGallery.style.cursor = '';
      document.body.style.userSelect = '';
    }
    
    const deltaX = clientX - this.galleryDragStartX;
    let newIndex = this.currentIndex;
    const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
    
    if (Math.abs(deltaX) > 30) {
      if (this.enableLoop) {
        // Loop behavior for end drag
        if (deltaX > 0) {
          newIndex = this.galleryDragStartIndex === 0 ? mainGalleryImageCount - 1 : this.galleryDragStartIndex - 1;
        } else if (deltaX < 0) {
          newIndex = (this.galleryDragStartIndex + 1) % mainGalleryImageCount;
        }
      } else {
        // Normal behavior for end drag
        if (deltaX > 0 && this.galleryDragStartIndex > 0) {
          newIndex = this.galleryDragStartIndex - 1;
        } else if (deltaX < 0 && this.galleryDragStartIndex < mainGalleryImageCount - 1) {
          newIndex = this.galleryDragStartIndex + 1;
        }
      }
      
      if (newIndex !== this.currentIndex) {
        this.goToImage(newIndex);
      }
    } else {
      this.goToImage(this.currentIndex);
    }
  }

  /**
   * Bind scrollbar events
   */
  bindScrollbarEvents() {
    if (!this.scrollbarContainer || !this.scrollbarThumb || !this.thumbnailTrack) return;

    let isDragging = false;

    this.scrollbarThumb.addEventListener('mousedown', (event) => {
      if (this.totalImages <= this.visibleThumbnails) return;
      
      event.preventDefault();
      isDragging = true;
      
      const initialMouseX = event.clientX;
      const initialThumbTransform = this.scrollbarThumb.style.transform;
      const initialThumbLeft = parseFloat(initialThumbTransform.match(/translateX\(([^)]+)px\)/)?.[1] || '0');
      
      this.scrollbarThumb.dataset.initialMouseX = initialMouseX.toString();
      this.scrollbarThumb.dataset.initialThumbLeft = initialThumbLeft.toString();
      
      this.scrollbarThumb.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      this.thumbnailTrack.style.scrollSnapType = 'none';
      this.thumbnailTrack.style.scrollBehavior = 'auto';
      this.scrollbarThumb.style.transition = 'none';
    });

    document.addEventListener('mousemove', (event) => {
      if (!isDragging || !this.thumbnailTrack) return;
      
      const scrollbarWidth = this.scrollbarContainer.offsetWidth;
      const containerWidth = this.thumbnailTrack.parentElement.getBoundingClientRect().width;
      const exactVisible = containerWidth / this.thumbnailWidth;
      const maxScrollIndex = Math.max(0, this.totalImages - exactVisible)
      
      const initialMouseX = parseFloat(this.scrollbarThumb.dataset.initialMouseX || '0');
      const initialThumbLeft = parseFloat(this.scrollbarThumb.dataset.initialThumbLeft || '0');
      
      const mouseDelta = event.clientX - initialMouseX;
      const newThumbLeft = Math.max(0, Math.min(scrollbarWidth - this.scrollbarThumb.offsetWidth, initialThumbLeft + mouseDelta));
      
      const thumbWidth = Math.max(20, (exactVisible / this.totalImages) * scrollbarWidth);
      const scrollRatio = newThumbLeft / (scrollbarWidth - thumbWidth);
      const newScrollIndex = scrollRatio * maxScrollIndex;
      
      const scrollX = newScrollIndex * this.thumbnailWidth;
      this.thumbnailTrack.scrollLeft = scrollX;
      
      this.scrollbarThumb.style.width = `${thumbWidth}px`;
      this.scrollbarThumb.style.transform = `translateX(${newThumbLeft}px)`;
    });

    document.addEventListener('mouseup', () => {
      if (!isDragging || !this.thumbnailTrack) return;
      
      isDragging = false;
      
      this.scrollbarThumb.style.cursor = 'grab';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      this.thumbnailTrack.style.scrollSnapType = 'x mandatory';
      this.thumbnailTrack.style.scrollBehavior = 'smooth';
      this.scrollbarThumb.style.transition = '';
    });

    this.scrollbarThumb.style.cursor = 'grab';
  }

  /**
   * Update main gallery position
   */
  updateGalleryPosition(instant = false) {
    if (!this.galleryTrack) return;
    const translateX = -this.currentIndex * 100;
    
    if (instant) {
      // Temporarily disable transition for instant updates
      const originalTransition = this.galleryTrack.style.transition;
      this.galleryTrack.style.transition = 'none';
      this.galleryTrack.style.transform = `translateX(${translateX}%)`;
      
      // Force reflow to ensure the transform is applied immediately
      this.galleryTrack.offsetHeight;
      
      // Restore original transition
      this.galleryTrack.style.transition = originalTransition;
    } else {
      this.galleryTrack.style.transform = `translateX(${translateX}%)`;
    }
    
    console.log('🎨 [Gallery] Updated main gallery position:', { currentIndex: this.currentIndex, translateX, instant });
  }

  /**
   * Update thumbnail position
   */
  updateThumbnailPosition(instant = false) {
    if (!this.thumbnailTrack || this.thumbnailWidth === 0) return;
    
    // In dots mode, no scrolling is needed as all dots are visible
    if (this.isDotsMode) {
      this.updateNavigationArrows(0);
      return;
    }
    
    let scrollIndex = 0;
    
    if (this.totalImages > this.visibleThumbnails) {
      if (this.currentIndex === 0) {
        scrollIndex = 0;
      } else if (this.currentIndex >= this.totalImages - this.visibleThumbnails) {
        scrollIndex = this.totalImages - this.visibleThumbnails;
      } else {
        scrollIndex = this.currentIndex;
      }
    } else {
      if (!this.scrollbarContainer) return;
      this.scrollbarContainer.style.display = "none";
    }
    
    const containerWidth = this.thumbnailTrack.parentElement.getBoundingClientRect().width;
    const exactVisible = containerWidth / this.thumbnailWidth;
    const maxScrollIndex = Math.max(0, this.totalImages - exactVisible);
    
    scrollIndex = Math.min(scrollIndex, maxScrollIndex);
    
    const scrollX = scrollIndex * this.thumbnailWidth;
    this.thumbnailTrack.scrollTo({
      left: scrollX,
      behavior: instant ? 'auto' : 'smooth'
    });
    
    this.updateNavigationArrows(scrollIndex);
    this.updateScrollbar();
  }

  /**
   * Update navigation arrows visibility
   */
  updateNavigationArrows(scrollIndex) {
    if (!this.thumbnailPrevButton || !this.thumbnailNextButton) return;
    
    if (scrollIndex > 0) {
      this.thumbnailPrevButton.style.display = 'block';
    } else {
      this.thumbnailPrevButton.style.display = 'none';
    }
    
    if (scrollIndex < this.totalImages - this.visibleThumbnails) {
      this.thumbnailNextButton.style.display = 'block';
    } else {
      this.thumbnailNextButton.style.display = 'none';
    }
  }

  /**
   * Update main gallery navigation arrows visibility
   */
  updateMainGalleryArrows() {
    if (!this.prevButton || !this.nextButton) return;
    
    // Use the actual number of main gallery images for navigation
    const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
    
    if (this.enableLoop) {
      // When looping is enabled, always show both arrows
      this.prevButton.style.display = 'flex';
      this.nextButton.style.display = 'flex';
    } else {
      // Normal behavior: hide arrows at boundaries
      // Hide previous arrow if we're at the first image
      if (this.currentIndex === 0) {
        this.prevButton.style.display = 'none';
      } else {
        this.prevButton.style.display = 'flex';
      }
      
      // Hide next arrow if we're at the last image
      if (this.currentIndex === mainGalleryImageCount - 1) {
        this.nextButton.style.display = 'none';
      } else {
        this.nextButton.style.display = 'flex';
      }
    }
  }

  /**
   * Update active thumbnail
   */
  updateActiveThumbnail() {
    console.log('🎨 [Gallery] Updating active thumbnail for index:', this.currentIndex);
    
    // Re-query buttons to ensure we have the latest DOM elements
    const regularThumbnails = document.querySelector('[data-regular-thumbnails]');
    let buttons = null;
    
    if (regularThumbnails) {
      buttons = document.querySelectorAll('[data-regular-thumbnails] [data-thumbnail-button]');
    } else {
      buttons = document.querySelectorAll(this.options.thumbnailButtonSelector);
    }
    
    if (!buttons || buttons.length === 0) {
      console.log('🎨 [Gallery] No thumbnail buttons found');
      return;
    }
    
    // Check if we're in dots mode (mobile and buttons have dot-button class)
    const isMobile = window.innerWidth <= 768;
    const firstButton = buttons[0];
    const hasDotButtonClass = firstButton && firstButton.classList.contains('dot-button');
    const isDots = isMobile && hasDotButtonClass;
    
    console.log('🎨 [Gallery] Found', buttons.length, 'thumbnail buttons, isDots:', isDots, 'isMobile:', isMobile, 'hasDotButtonClass:', hasDotButtonClass);
    
    buttons.forEach((button) => {
      const buttonIndex = parseInt(button.dataset.index);
      const isActive = buttonIndex === this.currentIndex;
      
      if (isDots) {
        // In dots mode, use the 'active' class instead of 'ring-2'
        if (isActive) {
          button.classList.add('active');
          console.log('🎨 [Gallery] Added active class to dot button at index:', buttonIndex);
        } else {
          button.classList.remove('active');
        }
        // Also remove ring-2 in case it was added
        button.classList.remove('ring-2');
        button.style.removeProperty('--tw-ring-color');
      } else {
        // Regular thumbnail mode
        if (isActive) {
          button.classList.add('ring-2');
          button.style.setProperty('--tw-ring-color', '#FFA001', 'important');
        } else {
          button.classList.remove('ring-2');
          button.style.removeProperty('--tw-ring-color');
        }
        // Also remove active class in case it was added
        button.classList.remove('active');
      }
    });
  }

  /**
   * Update scrollbar position
   */
  updateScrollbar() {
    if (!this.scrollbarThumb || !this.scrollbarContainer || !this.thumbnailTrack || this.totalImages <= this.visibleThumbnails || this.isDotsMode) return;
    
    const containerWidth = this.thumbnailTrack.parentElement.getBoundingClientRect().width;
    const exactVisible = containerWidth / this.thumbnailWidth;
    const maxScrollIndex = Math.max(0, this.totalImages - exactVisible);
    
    const currentScroll = this.thumbnailTrack.scrollLeft / this.thumbnailWidth;
    
    const scrollbarWidth = this.scrollbarContainer.offsetWidth;
    const thumbWidth = Math.max(20, (exactVisible / this.totalImages) * scrollbarWidth);
    const thumbPosition = (currentScroll / maxScrollIndex) * (scrollbarWidth - thumbWidth);
    
    this.scrollbarThumb.style.width = `${thumbWidth}px`;
    this.scrollbarThumb.style.transform = `translateX(${thumbPosition}px)`;
  }

  /**
   * Go to specific image
   */
  goToImage(index, instant = false) {
    console.log('🎨 [Gallery] goToImage called with index:', index, 'totalImages:', this.totalImages, 'instant:', instant);
    
    // Use the actual number of main gallery images for bounds checking
    const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
    
    if (index < 0 || index >= mainGalleryImageCount) {
      console.log('🎨 [Gallery] Invalid index, returning');
      return;
    }
    this.currentIndex = index;
    console.log('🎨 [Gallery] Updated currentIndex to:', this.currentIndex);
    this.updateGalleryPosition(instant);
    this.updateThumbnailPosition(instant);
    this.updateActiveThumbnail();
    this.updateScrollbar();
    this.updateMainGalleryArrows();
    
    // Emit gallery change event for video auto-play
    const galleryChangeEvent = new CustomEvent('gallery:change', {
      detail: { index: this.currentIndex },
      bubbles: true
    });
    document.dispatchEvent(galleryChangeEvent);
    console.log('🎨 [Gallery] Emitted gallery:change event for index:', this.currentIndex);
  }

  /**
   * Go to next image
   */
  nextImage() {
    const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
    
    if (this.enableLoop) {
      // Loop behavior: if at last image, go to first
      const nextIndex = (this.currentIndex + 1) % mainGalleryImageCount;
      this.goToImage(nextIndex);
    } else {
      // Normal behavior: only go next if not at last image
      if (this.currentIndex < mainGalleryImageCount - 1) {
        this.goToImage(this.currentIndex + 1);
      }
    }
  }

  /**
   * Go to previous image
   */
  prevImage() {
    const mainGalleryImageCount = this.productImages ? this.productImages.length : 0;
    
    if (this.enableLoop) {
      // Loop behavior: if at first image, go to last
      const prevIndex = this.currentIndex === 0 ? mainGalleryImageCount - 1 : this.currentIndex - 1;
      this.goToImage(prevIndex);
    } else {
      // Normal behavior: only go previous if not at first image
      if (this.currentIndex > 0) {
        this.goToImage(this.currentIndex - 1);
      }
    }
  }

  /**
   * Scroll thumbnails
   */
  scrollThumbnails(direction) {
    // In dots mode, no scrolling is needed
    if (this.isDotsMode) return;
    
    const currentScroll = this.thumbnailTrack.scrollLeft / this.thumbnailWidth;
    let newScroll;
    
    if (direction === -1) {
      newScroll = Math.max(0, currentScroll - 1);
    } else {
      const maxScroll = Math.max(0, this.totalImages - this.visibleThumbnails);
      newScroll = Math.min(maxScroll, currentScroll + 1);
    }
    
    this.thumbnailTrack.scrollTo({
      left: newScroll * this.thumbnailWidth,
      behavior: 'smooth'
    });
  }

  /**
   * Find thumbnail for variant
   */
  findThumbnailForVariant(variantId) {
    console.log('🔍 [Gallery] Finding thumbnail for variant:', variantId);
    
    if (!this.thumbnailButtons || this.thumbnailButtons.length === 0) {
      console.log('🔍 [Gallery] No thumbnails available, returning 0');
      return 0;
    }
    
    // Debug: Log all thumbnail buttons and their variant data
    console.log('🔍 [Gallery] Available thumbnails:', Array.from(this.thumbnailButtons).map(button => ({
      index: button.dataset.index,
      variantImages: button.dataset.variantImages,
      hasVariantId: button.dataset.variantImages && button.dataset.variantImages.includes(variantId.toString())
    })));
    
    const thumbnailButton = Array.from(this.thumbnailButtons).find(button => {
      const variantImages = button.dataset.variantImages;
      const hasVariant = variantImages && variantImages.includes(variantId.toString());
      console.log('🔍 [Gallery] Checking button:', {
        index: button.dataset.index,
        variantImages,
        searchingFor: variantId.toString(),
        hasVariant
      });
      return hasVariant;
    });
    
    if (thumbnailButton) {
      const index = parseInt(thumbnailButton.dataset.index);
      console.log('🔍 [Gallery] Found thumbnail at index:', index);
      return index;
    }
    
    console.log('🔍 [Gallery] No thumbnail found for variant, defaulting to 0');
    return 0;
  }
} 